import { Outlet, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, FolderKanban, ListChecks, Settings, LogOut, Languages } from "lucide-react";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useAppSelector } from "../store/hooks";
import WorkspaceSelector from "./WorkspaceSelector";
import { Button } from "./ui/button";
import storage from "../utils/storage";
import { STORAGE_KEYS } from "../utils/constants";

export default function Root() {
  const location = useLocation();
  const { currentUser, logout } = useApp();
  const { language, setLanguage, t } = useLanguage();
  const { currentRole, workspaces } = useAppSelector((state) => state.workspace);

  // Use workspace role from Redux if available, fallback to localStorage, then context
  const storedRole = storage.get(STORAGE_KEYS.CURRENT_ROLE);
  const userRole = currentRole || storedRole || currentUser?.role;

  // ゲストユーザーの場合はタスクメニューのみ表示
  const navItems = userRole === 'guest' 
    ? [
        { path: "/tasks", label: t('nav.tasks'), icon: ListChecks },
      ]
    : [
        { path: "/", label: t('nav.dashboard'), icon: LayoutDashboard },
        { path: "/projects", label: t('nav.projects'), icon: FolderKanban },
        { path: "/tasks", label: t('nav.tasks'), icon: ListChecks },
        { path: "/settings", label: t('nav.settings'), icon: Settings },
      ];

  const handleLogout = () => {
    logout();
  };

  const toggleLanguage = () => {
    setLanguage(language === 'ja' ? 'en' : 'ja');
  };

  const getRoleLabel = (role: string) => {
    if (role === 'admin') return t('roles.admin');
    if (role === 'member') return t('roles.member');
    if (role === 'guest') return t('roles.guest');
    return role;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* ロゴとタイトル */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">G</span>
                </div>
                <h1 className="text-gray-900">GRIT</h1>
              </div>
              
              <WorkspaceSelector />
            </div>
            
            <div className="flex items-center gap-4">
              <nav className="flex items-center gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        isActive
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
              
              <div className="flex items-center gap-3 border-l border-gray-200 pl-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleLanguage}
                  className="text-gray-600"
                  title={language === 'ja' ? 'Switch to English' : '日本語に切り替え'}
                >
                  <Languages className="w-4 h-4 mr-2" />
                  {language === 'ja' ? 'EN' : 'JA'}
                </Button>
                
                <div className="text-right">
                  <p className="text-sm text-gray-900">{currentUser?.name}</p>
                  <p className="text-xs text-gray-500">
                    {userRole && getRoleLabel(userRole)}
                  </p>
                  <p className="text-[10px] text-gray-400">v{__APP_VERSION__}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-gray-600"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main>
        <Outlet />
      </main>
    </div>
  );
}

